import React, { useEffect, useState } from "react";
import useApi from "../../../CustomHooks/useApi";
import { useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import {
    fetchWalletBalance,
    fetchAepsBalance,
    fetchSettlementBalance,
} from "../../../Store/Slices/WalletSlice";

const UPI_SUFFIX_MAP = {
    PHONEPE: ["ybl", "ibl", "axl"],
    GOOGLEPAY: ["okicici", "okaxis", "okhdfc", "oksbi"],
    PAYTM: ["pthdfc", "ptyes", "ptaxis", "ptsbi"],
    AMAZON: ["apl"],
};

const AepsRemitter2 = () => {

    const location = useLocation();
    const remitter_mobile = location.state?.mobile;
    const remitter_name = location.state?.name;
    const mid = useSelector((s) => s.auth.employeeDetails?.username);
    const onetimelimit = useSelector((store) => store?.auth?.employeeDetails?.oneTimeLimit);
    const navigate = useNavigate();
    const dispatch = useDispatch();

    /* ================= STATES ================= */
    const [upiList, setUpiList] = useState([]);
    const [loading, setLoading] = useState(false);
    const [addMode, setAddMode] = useState("VERIFY"); // VERIFY | DIRECT
    const [manualBeneName, setManualBeneName] = useState("");

    const [showUpiPopup, setShowUpiPopup] = useState(false);
    const [showVerifyConfirm, setShowVerifyConfirm] = useState(false);
    const [customerDetails, setCustomerDetails] = useState(null);

    const [upiType, setUpiType] = useState("BANK");
    const [vpa, setVpa] = useState("");
    const [upiSuffix, setUpiSuffix] = useState("");
    const [beneName, setBeneName] = useState("");
    const [msg, setMsg] = useState("");
    const [amountMap, setAmountMap] = useState({});

    const [isVerified, setIsVerified] = useState(false);
    const [verifying, setVerifying] = useState(false);
    const [amountErrorMap, setAmountErrorMap] = useState({});
    const [confirmType, setConfirmType] = useState(null);


    /* ================= API HOOKS ================= */
    const { callApi: fetchBeneList } = useApi("/payout/bene/list", "GET");
    const { callApi: fetchCustomerDetails } = useApi(
        "/payout/customer/details",
        "GET"
    );
    const { callApi: verifyUpiApi } = useApi("/payout/verify", "POST");
    const { callApi: addUpiApi } = useApi("/payout/bene/verify", "POST");

    /* ================= LOAD BENE UPI LIST ================= */
    useEffect(() => {
        if (remitter_mobile) {
            getUpiList();
            getCustomerDetails();
        }
    }, [remitter_mobile]);

    const getUpiList = async () => {
        setLoading(true);
        try {
            const res = await fetchBeneList(null, `?mobile=${remitter_mobile}`);
            const list = Array.isArray(res) ? res : res?.data || [];
            setUpiList(list);
        } catch (e) {
            console.error("BENE LIST ERROR", e);
            setUpiList([]);
        }
        setLoading(false);
    };

    const getCustomerDetails = async () => {
        try {
            const res = await fetchCustomerDetails(null, `?mobile=${remitter_mobile}`);
            setCustomerDetails(res);
        } catch (err) {
            console.error("CUSTOMER DETAILS ERROR", err);
        }
    };


    /* ================= VERIFY BUTTON CLICK ================= */
    const handleVerifyUpi = () => {
        if (!vpa) return setMsg("Enter UPI ID first");
        setShowVerifyConfirm(true);
    };

    /* ================= CONFIRM VERIFY API ================= */
    const verifyAndAddUpi = async () => {
        setVerifying(true);
        setMsg("");

        try {
            /* ================= VERIFY UPI ================= */
            const verifyRes = await verifyUpiApi({ upiId: vpa, mid });

            if (verifyRes?.status !== "SUCCESS" || !verifyRes?.accountExists) {
                setMsg(verifyRes?.message || "UPI verification failed");
                setShowVerifyConfirm(false)
                return;
            }

            const verifiedName = verifyRes?.nameAtBank || "";
            setBeneName(verifiedName);

            const addRes = await addUpiApi({
                customerMobile: remitter_mobile,
                upiId: vpa,
                beneName: verifiedName,
                isVerified: true,
            });

            if (addRes?.verified === true) {
                setMsg(addRes?.message || "UPI added successfully");

                setShowVerifyConfirm(false); // close confirm popup
                setShowUpiPopup(false);      // close main popup

                getUpiList();
                getCustomerDetails();
            } else {
                setMsg(addRes?.message || "Failed to add UPI");
            }

        } catch (err) {
            console.error("VERIFY & ADD ERROR:", err);
            setMsg("Something went wrong. Please try again.");
        } finally {
            setVerifying(false);
            dispatch(fetchWalletBalance());
            dispatch(fetchAepsBalance());
            dispatch(fetchSettlementBalance());
        }
    };




    const handleSendMoney = (bene) => {
        const amount = amountMap[bene.id];

        if (!amount || Number(amount) <= 0) {
            alert("Please enter a valid amount");
            return;
        }

        navigate("/aepsfundtransfer2", {
            state: {
                amount: Number(amount),
                customerMobile: remitter_mobile,
                selectedBeneficiary: [
                    {
                        beneficiaryName: bene.beneName,
                        accountNo: bene.upiId,
                        ifscCode: "UPI",
                        bankName: bene.bankName,
                    },
                ],
            },
        });
    };


    const addUpiDirectly = async () => {
        if (!vpa) {
            setMsg("Enter UPI ID");
            return;
        }

        if (!manualBeneName) {
            setMsg("Enter beneficiary name");
            return;
        }

        setVerifying(true);
        setMsg("");

        try {
            const res = await addUpiApi({
                customerMobile: remitter_mobile,
                upiId: vpa,
                beneName: manualBeneName,
                isVerified: false,
            });

            if (res?.status === "SUCCESS") {
                setMsg(res?.message);

                setShowVerifyConfirm(false); // close confirm popup
                setShowUpiPopup(false);      // close main popup

                getUpiList();
                getCustomerDetails();
            } else {
                setMsg(res?.message || "Failed to add UPI");
            }
        } catch (err) {
            console.error("DIRECT ADD ERROR", err);
            setMsg("Something went wrong");
        } finally {
            setVerifying(false);
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-indigo-50 to-cyan-50 py-10">
            <div className="max-w-7xl mx-auto px-4 space-y-8">

                {/* ================= REMITTER PROFILE ================= */}
                <div className="bg-white/90 backdrop-blur-xl rounded-2xl border shadow-lg px-6 py-4">
                    <div className="flex items-center justify-between">

                        {/* LEFT SIDE NAME + MOBILE */}
                        <div className="flex items-center gap-2 text-lg font-semibold text-slate-800">
                            <span>
                                {customerDetails?.name}
                            </span>

                            <span className="text-slate-400">|</span>

                            <span className="text-indigo-600">
                                {customerDetails?.mobile}
                            </span>
                        </div>

                        {/* RIGHT SIDE BUTTON */}
                        <button
                            onClick={() => {
                                setShowUpiPopup(true);
                                setVpa("");
                                setBeneName("");
                                setManualBeneName("");
                                setMsg("");
                                setIsVerified(false);
                                setUpiType("BANK");
                                setUpiSuffix("");
                                setAddMode("VERIFY");
                            }}
                            className="px-4 py-2 text-sm font-semibold bg-[#12345A] text-white rounded-lg shadow hover:shadow-md transition"
                        >
                            + Add UPI
                        </button>

                    </div>
                </div>



                {/* ================= BENE LIST TABLE desktp view================= */}

                <div className="bg-white rounded-3xl border border-gray-400 shadow-sm overflow-x-auto overflow-y-auto hidden md:block">
                    <table className="w-full text-sm 2xl:text-base min-[2000px]:text-[22px] text-left border-collapse">
                        <thead className="bg-[#12345A] text-white">
                            <tr>
                                {["Name", "UPI ID", "Bank", "Status", "Amount", "Action"].map((h) => (
                                    <th key={h} className="px-4 py-3 font-semibold border border-gray-400">
                                        {h}
                                    </th>
                                ))}
                            </tr>
                        </thead>

                        <tbody className="divide-y divide-slate-200">
                            {loading ? (
                                <tr>
                                    <td colSpan="6" className="py-10 text-center text-slate-500 border border-gray-400">
                                        Loading beneficiaries…
                                    </td>
                                </tr>
                            ) : upiList.length === 0 ? (
                                <tr>
                                    <td colSpan="6" className="py-10 text-center text-slate-500 border border-gray-400">
                                        No Beneficiary Found
                                    </td>
                                </tr>
                            ) : (
                                upiList.map((item) => {
                                    const amount = Number(amountMap[item.id]);
                                    const isValidAmount =
                                        amount >= 100 &&
                                        amount <= onetimelimit &&
                                        !amountErrorMap[item.id];

                                    return (
                                        <tr key={item.id} className="hover:bg-slate-50 transition">

                                            {/* NAME */}
                                            <td className="px-4 py-3 font-medium border border-gray-400">
                                                {item.beneName}
                                            </td>

                                            {/* UPI ID */}
                                            <td className="px-4 py-3 border border-gray-400 font-semibold text-slate-600">
                                                {item.upiId}
                                            </td>

                                            {/* BANK */}
                                            <td className="px-4 py-3 border border-gray-400">
                                                {item.bankName}
                                            </td>

                                            {/* STATUS */}
                                            <td className="px-4 py-3 border border-gray-400">
                                                {item.isVerified ? (
                                                    <span className="px-3 py-1 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-700">
                                                        ✔ Verified
                                                    </span>
                                                ) : (
                                                    <span className="px-3 py-1 rounded-full text-xs font-semibold bg-rose-100 text-rose-700">
                                                        Not Verified
                                                    </span>
                                                )}
                                            </td>

                                            {/* AMOUNT INPUT */}
                                            <td className="px-4 py-3 align-top border border-gray-400">

                                                <div className="flex flex-col items-center  justify-center">

                                                    <input
                                                        type="text"
                                                        inputMode="numeric"
                                                        placeholder="₹ Amount"
                                                        value={amountMap[item.id] || ""}
                                                        onChange={(e) => {
                                                            const value = e.target.value;
                                                            if (value === "") {
                                                                setAmountMap((prev) => ({ ...prev, [item.id]: "" }));
                                                                setAmountErrorMap((prev) => ({ ...prev, [item.id]: "" }));
                                                                return;
                                                            }
                                                            const num = Number(value);
                                                            if (num < 100) {
                                                                setAmountErrorMap((prev) => ({ ...prev, [item.id]: "Minimum ₹100" }));
                                                            } else if (num > onetimelimit) {
                                                                setAmountErrorMap((prev) => ({ ...prev, [item.id]: `Max ${onetimelimit}` }));
                                                            } else {
                                                                setAmountErrorMap((prev) => ({ ...prev, [item.id]: "" }));
                                                            }
                                                            if (!isNaN(num) && num <= onetimelimit) {
                                                                setAmountMap((prev) => ({ ...prev, [item.id]: value }));
                                                            }
                                                        }}
                                                        onKeyDown={(e) => {
                                                            if (e.key === 'Enter') {
                                                                e.preventDefault();
                                                                if (isValidAmount) handleSendMoney(item);
                                                            }
                                                        }}
                                                        className={`w-28 px-3 py-2 border rounded-lg text-sm outline-none transition
                                            ${amountErrorMap[item.id]
                                                                ? "border-red-500 focus:border-red-600"
                                                                : "border-slate-300 focus:border-indigo-500"}`}
                                                    />
                                                    {/* <div className="h-4 mt-1">
                                        {amountErrorMap[item.id] && (
                                            <p className="text-[10px] text-red-600 leading-tight">
                                                {amountErrorMap[item.id]}
                                            </p>
                                        )}
                                    </div> */}
                                                    {amountErrorMap[item.id] && (
                                                        <p className="text-[10px] text-red-600 font-semibold mt-1 leading-tight text-center">
                                                            {amountErrorMap[item.id]}
                                                        </p>
                                                    )}
                                                </div>
                                            </td>

                                            {/* ACTION */}
                                            <td className="px-4 py-3 border border-gray-400">
                                                <button
                                                    onClick={() => handleSendMoney(item)}
                                                    disabled={!isValidAmount}
                                                    className={`px-4 py-2 text-xs font-semibold rounded-lg transition
                                        ${isValidAmount
                                                            ? "bg-indigo-600 text-white hover:bg-indigo-700 shadow-md"
                                                            : "bg-slate-200 text-slate-400 cursor-not-allowed"
                                                        }`}
                                                >
                                                    Send Money
                                                </button>
                                            </td>
                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>


                {/* =========================================
        mobile view
        Visible on mobile, hidden on md (768px) and up
    ========================================= */}
                <div className="block md:hidden flex flex-col gap-4">
                    {loading ? (
                        <div className="py-10 text-center text-slate-500 bg-white rounded-2xl border border-gray-300 shadow-sm">
                            Loading beneficiaries…
                        </div>
                    ) : upiList.length === 0 ? (
                        <div className="py-10 text-center text-slate-500 bg-white rounded-2xl border border-gray-300 shadow-sm">
                            No Beneficiary Found
                        </div>
                    ) : (
                        upiList.map((item) => {
                            const amount = Number(amountMap[item.id]);
                            const isValidAmount =
                                amount >= 100 &&
                                amount <= onetimelimit &&
                                !amountErrorMap[item.id];

                            return (
                                <div key={item.id} className="bg-white p-4 rounded-2xl shadow-sm border border-gray-300 flex flex-col gap-3">

                                    {/* Top Row: Name and Status */}
                                    <div className="flex justify-between items-start">
                                        <span className="font-bold text-slate-800 text-lg leading-tight">
                                            {item.beneName}
                                        </span>
                                        {item.isVerified ? (
                                            <span className="px-2 py-1 rounded-md text-[10px] font-bold bg-emerald-100 text-emerald-700 whitespace-nowrap">
                                                ✔ Verified
                                            </span>
                                        ) : (
                                            <span className="px-2 py-1 rounded-md text-[10px] font-bold bg-rose-100 text-rose-700 whitespace-nowrap">
                                                Not Verified
                                            </span>
                                        )}
                                    </div>

                                    {/* Middle Row: Details */}
                                    <div className="flex flex-col text-sm text-slate-600 gap-1  p-2 rounded-lg border border-slate-100">
                                        <div className="flex gap-2">
                                            <span className="font-medium">UPI:</span>
                                            <span className="font-semibold text-slate-800">{item.upiId}</span>
                                        </div>
                                        <div className="flex gap-1">
                                            <span className="font-medium">Bank:</span>
                                            <span className="text-right">{item.bankName}</span>
                                        </div>
                                    </div>

                                    {/* Bottom Row: Input and Action Button */}
                                    <div className="flex items-start gap-2 mt-1">
                                        <div className="flex-1 flex flex-col">
                                            <input
                                                type="text"
                                                inputMode="numeric"
                                                placeholder="₹ Amount"
                                                value={amountMap[item.id] || ""}
                                                onChange={(e) => {
                                                    const value = e.target.value;
                                                    if (value === "") {
                                                        setAmountMap((prev) => ({ ...prev, [item.id]: "" }));
                                                        setAmountErrorMap((prev) => ({ ...prev, [item.id]: "" }));
                                                        return;
                                                    }
                                                    const num = Number(value);
                                                    if (num < 100) {
                                                        setAmountErrorMap((prev) => ({ ...prev, [item.id]: "Min ₹100" }));
                                                    } else if (num > onetimelimit) {
                                                        setAmountErrorMap((prev) => ({ ...prev, [item.id]: `Max ${onetimelimit}` }));
                                                    } else {
                                                        setAmountErrorMap((prev) => ({ ...prev, [item.id]: "" }));
                                                    }
                                                    if (!isNaN(num) && num <= onetimelimit) {
                                                        setAmountMap((prev) => ({ ...prev, [item.id]: value }));
                                                    }
                                                }}
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter') {
                                                        e.preventDefault();
                                                        if (isValidAmount) handleSendMoney(item);
                                                    }
                                                }}
                                                className={`w-full px-3 py-2 border rounded-lg text-sm outline-none transition
                                        ${amountErrorMap[item.id]
                                                        ? "border-red-500 focus:border-red-600"
                                                        : "border-slate-300 focus:border-indigo-500"}`}
                                            />
                                            {amountErrorMap[item.id] && (
                                                <p className="text-[10px] text-red-600 font-semibold mt-1 leading-tight">
                                                    {amountErrorMap[item.id]}
                                                </p>
                                            )}
                                        </div>

                                    </div>
                                    <button
                                        onClick={() => handleSendMoney(item)}
                                        disabled={!isValidAmount}
                                        className={`px-4 py-2 text-sm font-semibold rounded-lg transition whitespace-nowrap h-[38px]
                                    ${isValidAmount
                                                ? "bg-indigo-600 text-white hover:bg-indigo-700 shadow-md"
                                                : "bg-slate-200 text-slate-400 cursor-not-allowed"
                                            }`}
                                    >
                                        Send
                                    </button>

                                </div>
                            );
                        })
                    )}
                </div>

                {/* </div> */}

            </div>

            {/* ================= ADD UPI MODAL ================= */}
            {showUpiPopup && (
                <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[999] px-6 ">
                    <div className="bg-white w-full max-w-lg rounded-2xl shadow-2xl overflow-hidden">

                        {/* HEADER */}
                        <div className="px-6 py-4 bg-gradient-to-r from-indigo-600 to-cyan-500 text-white flex justify-between items-center">
                            <h2 className="text-lg font-semibold">Add UPI Beneficiary</h2>
                            <button
                                onClick={() => setShowUpiPopup(false)}
                                className="text-white text-xl leading-none"
                            >
                                ×
                            </button>
                        </div>

                        {/* BODY */}
                        <div className="p-6 space-y-6">

                            {/* ADD MODE */}
                            {/* <div>
                                <p className="text-sm font-semibold mb-2">Choose Add Mode</p>
                                <div className="grid grid-cols-2 gap-3">
                                    <button
                                        onClick={() => setAddMode("VERIFY")}
                                        className={`py-3 rounded-xl border font-semibold transition
              ${addMode === "VERIFY"
                                                ? "bg-indigo-600 text-white shadow"
                                                : "bg-white border-slate-300"}`}
                                    >
                                        Verify UPI<br />
                                        <span className="text-xs opacity-80">₹3 Charge</span>
                                    </button>

                                    <button
                                        onClick={() => setAddMode("DIRECT")}
                                        className={`py-3 rounded-xl border font-semibold transition
              ${addMode === "DIRECT"
                                                ? "bg-emerald-600 text-white shadow"
                                                : "bg-white border-slate-300"}`}
                                    >
                                        Add Without Verify<br />
                                        <span className="text-xs opacity-80">₹0 Charge</span>
                                    </button>
                                </div>
                            </div> */}

                            {/* PROVIDER */}
                            <div>
                                <p className="text-sm font-semibold mb-2">UPI Provider</p>
                                <div className="flex flex-wrap gap-2">
                                    {["BANK", "PHONEPE", "GOOGLEPAY", "PAYTM", "AMAZON"].map(type => (
                                        <button
                                            key={type}
                                            onClick={() => {
                                                setUpiType(type);
                                                setUpiSuffix("");
                                                setVpa("");
                                                setIsVerified(false);
                                            }}
                                            className={`px-4 py-2 rounded-xl border text-sm font-medium
                ${upiType === type
                                                    ? "bg-indigo-600 text-white"
                                                    : "bg-white border-slate-300"}`}
                                        >
                                            {type}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {/* UPI INPUT */}
                            <div>
                                <label className="text-sm font-semibold">UPI ID</label>
                                <input
                                    value={vpa}
                                    onChange={(e) => {
                                        setVpa(e.target.value);
                                        setIsVerified(false);
                                    }}
                                    placeholder="example@upi"
                                    className="w-full px-4 py-3 mt-2 rounded-xl border
            focus:ring-2 focus:ring-indigo-500 outline-none"
                                />

                                {UPI_SUFFIX_MAP[upiType] && (
                                    <div className="mt-3 flex flex-wrap gap-2">
                                        {UPI_SUFFIX_MAP[upiType].map(suffix => (
                                            <button
                                                key={suffix}
                                                onClick={() => {
                                                    const base = vpa.split("@")[0] || "";
                                                    setVpa(`${base}@${suffix}`);
                                                    setIsVerified(false);
                                                }}
                                                className="px-3 py-1.5 rounded-full border text-xs
                  hover:bg-indigo-50 hover:border-indigo-400"
                                            >
                                                @{suffix}
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>

                            {/* BENEFICIARY NAME */}
                            <div>
                                <label className="text-sm font-semibold">
                                    Beneficiary Name
                                    {addMode === "VERIFY" && (
                                        <span className="text-xs text-slate-500 ml-2">
                                            (Auto-filled after verification)
                                        </span>
                                    )}
                                </label>

                                {addMode === "VERIFY" ? (
                                    <input
                                        value={beneName}
                                        disabled
                                        className="w-full px-4 py-3 mt-2 rounded-xl border bg-gray-100"
                                    />
                                ) : (
                                    <input
                                        value={manualBeneName}
                                        onChange={(e) => {
                                            const value = e.target.value;

                                            // ✅ Allow only alphabets + space
                                            if (/^[a-zA-Z\s]*$/.test(value)) {
                                                setManualBeneName(value);
                                            }
                                        }}
                                        placeholder="Enter beneficiary name"
                                        className="w-full px-4 py-3 mt-2 rounded-xl border focus:ring-2 focus:ring-emerald-500 outline-none"
                                        autoComplete="off"
                                        name="beneficiary_name_random" // ❗ avoid autofill
                                    />
                                )}
                            </div>

                            {/* MESSAGE SLOT (NO JUMP) */}
                            <div className="h-5">
                                {msg && <p className="text-sm text-red-600">{msg}</p>}
                            </div>
                        </div>

                        {/* FOOTER */}
                        {/* <div className="px-6 py-4 bg-slate-50 flex justify-end gap-3">
                            <button
                                onClick={() => setShowUpiPopup(false)}
                                className="px-5 py-2 border rounded-xl"
                            >
                                Cancel
                            </button>

                            {addMode === "VERIFY" ? (
                                <button
                                    onClick={handleVerifyUpi}
                                    className="px-6 py-2 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700"
                                >
                                    Verify & Add ₹3
                                </button>
                            ) : (
                                <button
                                    onClick={addUpiDirectly}
                                    disabled={verifying}
                                    className="px-6 py-2 bg-emerald-600 text-white rounded-xl hover:bg-emerald-700"
                                >
                                    {verifying ? "Adding..." : "Add UPI"}
                                </button>
                            )}
                        </div> */}

                        <div className="px-6 py-4 bg-slate-50  flex flex-col sm:flex justify-center sm:justify-end gap-3">



                            {/* VERIFY */}
                            <button
                                onClick={() => {
                                    if (!vpa) {
                                        setMsg("Enter UPI ID");
                                        return;
                                    }

                                    setAddMode("VERIFY");
                                    setConfirmType("VERIFY");
                                    setShowVerifyConfirm(true);   // ✅ popup open
                                }}
                                className="px-6 py-2 rounded-xl font-semibold text-white
               bg-indigo-600 hover:bg-indigo-700"
                            >
                                Verify & Add ₹3
                            </button>
                            {/* ADD WITHOUT VERIFY */}
                            <button
                                onClick={() => {
                                    if (!vpa) {
                                        setMsg("Enter UPI ID");
                                        return;
                                    }
                                    setAddMode("DIRECT");
                                    if (!manualBeneName) {
                                        setMsg("Enter beneficiary name");
                                        return;
                                    }

                                    setConfirmType("DIRECT");
                                    setShowVerifyConfirm(true); // ✅ popup open
                                }}
                                className="px-5 py-2 rounded-xl font-semibold text-white
             bg-red-600 hover:bg-red-700 active:bg-red-800"
                            >
                                Add Without Verify ₹0
                            </button>


                        </div>



                    </div>
                </div>
            )}


            {/* ================= VERIFY CONFIRM MODAL ================= */}
            {showVerifyConfirm && (
                <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[1000]">
                    <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6">

                        {/* Title */}
                        <h3 className="text-lg font-semibold text-gray-900">
                            {confirmType === "VERIFY"
                                ? "Verify UPI ID / UPI ID सत्यापित करें"
                                : "Add UPI Without Verification / बिना सत्यापन के UPI जोड़ें"}
                        </h3>

                        {/* Description */}
                        <p className="text-sm text-slate-600 mt-2 leading-relaxed">
                            {confirmType === "VERIFY" ? (
                                <>
                                    ₹3 will be deducted for UPI verification.
                                    <br />
                                    <span className="text-xs text-gray-500">
                                        (UPI सत्यापन के लिए ₹3 काटे जाएंगे।)
                                    </span>
                                </>
                            ) : (
                                <>
                                    No charge will be deducted. UPI will be added without verification.
                                    <br />
                                    <span className="text-xs text-gray-500">
                                        (कोई शुल्क नहीं काटा जाएगा। UPI बिना सत्यापन के जोड़ दिया जाएगा।)
                                    </span>
                                </>
                            )}
                        </p>

                        {/* UPI Display */}
                        <div className="mt-4 p-3 bg-gray-50 border rounded-lg text-sm">
                            <b>UPI ID / UPI आईडी:</b> {vpa}
                        </div>

                        {/* Actions */}
                        <div className="flex justify-end gap-3 mt-6">
                            <button
                                onClick={() => setShowVerifyConfirm(false)}
                                className="px-4 py-2 border rounded-lg text-sm hover:bg-gray-50"
                            >
                                Cancel / रद्द करें
                            </button>

                            <button
                                disabled={verifying}
                                onClick={() => {
                                    if (confirmType === "VERIFY") {
                                        verifyAndAddUpi();
                                    } else {
                                        addUpiDirectly();
                                    }
                                }}
                                className={`px-5 py-2 rounded-lg text-white text-sm font-semibold disabled:opacity-60
            ${confirmType === "VERIFY"
                                        ? "bg-green-600 hover:bg-green-700"
                                        : "bg-emerald-600 hover:bg-emerald-700"
                                    }`}
                            >
                                {verifying
                                    ? "Processing... / प्रक्रिया जारी है..."
                                    : confirmType === "VERIFY"
                                        ? "Confirm & Verify / पुष्टि करें और सत्यापित करें"
                                        : "Confirm & Add / पुष्टि करें और जोड़ें"}
                            </button>
                        </div>
                    </div>
                </div>
            )}



        </div>
    );
};

export default AepsRemitter2;
